import React, { useEffect, useRef } from 'react';
import { cn } from 'lib/utils';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, autoResize = false, className, id, value, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    useEffect(() => {
      if (autoResize && textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }, [value, autoResize]);

    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-[12.5px] font-medium leading-[18px] text-foreground/80"
          >
            {label}
          </label>
        )}
        <textarea
          ref={(node) => {
            textareaRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          id={inputId}
          value={value}
          className={cn(
            // Matches Input's shape and horizontal padding so a textarea and a
            // text field stacked in the same form read as one control family.
            // text-base (16px) on mobile prevents iOS Safari's focus-zoom; md:text-sm restores the denser desktop size.
            'w-full rounded-md border border-border bg-card px-3 py-2 text-base text-foreground outline-none transition-colors md:text-sm',
            'placeholder:text-muted-foreground',
            'focus:border-foreground/30',
            'disabled:cursor-not-allowed disabled:opacity-50',
            autoResize && 'resize-none overflow-hidden',
            error && 'border-destructive focus:border-destructive',
            className
          )}
          {...props}
        />
        {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
        {error && <p className="text-xs text-error-700">{error}</p>}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
export default Textarea;
